import { Client, IMessage } from '@stomp/stompjs';
import { IFrame } from '@stomp/stompjs/src/i-frame';

import { uint8ArrayToUtf8 } from '@utils/websocket';

import { INotification } from '@interfaces/notifications/notifications';

import { heartbeatIncoming, heartbeatOutgoing, reconnectDelay } from '@constants/websocket';

import { URL } from './urls';

const {
	websocketURL,
	websocketNotification,
} = URL;

const WEBSOCKET_SERVER_URL = process.env.REACT_APP_WEBSOCKET_SERVER_URL;

interface IActions {
	onNotifications: (notification: INotification) => void;
}

class WebsocketService {
	stompClient: Client;

	constructor() {
		this.stompClient = new Client({
			reconnectDelay,
			heartbeatIncoming,
			heartbeatOutgoing,
			forceBinaryWSFrames: false,
			onWebSocketError: (err: Error) => {
				// eslint-disable-next-line no-console
				console.debug(err);

				const sessionToken = sessionStorage.getItem('token');

				if (this.stompClient && sessionToken) {
					this.stompClient.brokerURL = `${WEBSOCKET_SERVER_URL}?access_token=${sessionToken}`;

					return;
				}

				try {
					this.disconnectWebsocket();
				} catch (e) {
					// eslint-disable-next-line no-console
					console.debug(e);

					this.stompClient.forceDisconnect();
				}
			},
			debug: (str: string) => {
				// eslint-disable-next-line no-console
				console.debug(str);
			},
			onDisconnect: () => {
				// eslint-disable-next-line no-console
				console.debug('onDisconnect');
			},
			onStompError: (receipt: IFrame) => {
				// eslint-disable-next-line no-console
				console.debug('Broker reported error: ' + receipt.headers.message);
				// eslint-disable-next-line no-console
				console.debug('Additional details: ' + receipt.body);
			},
		});
	}

	onConnect(userId: string, actions: { onNotifications: any }) {
		// eslint-disable-next-line no-console
		console.log('onConnect');

		const urlsCommonPart = `${websocketURL}/${userId}`;

		this.stompClient?.subscribe(
			`${urlsCommonPart}/${websocketNotification}`,
			(notification: IMessage) => {
				const decodedNotification = uint8ArrayToUtf8(notification.binaryBody);

				actions.onNotifications(decodedNotification);
			},
		);

	}

	connectToWebsocket(userId: string, sessionToken: string | null, actions: IActions) {
		if (this.stompClient && this.stompClient.connected) return;

		this.stompClient.brokerURL = `${WEBSOCKET_SERVER_URL}?access_token=${sessionToken}`;
		this.stompClient.onConnect = () => {
			this.onConnect(userId, actions);
		};

		this.stompClient?.activate();
	}

	async disconnectWebsocket() {
		if (!this.stompClient || !this.stompClient.connected) {
			return;
		}

		try {
			await this.stompClient.deactivate();
		} catch (e) {
			this.stompClient.forceDisconnect();
		}
	}
}

export default new WebsocketService();
